import { useEffect, useState, type FormEvent } from 'react';
import { Button, ErrorState } from '@helios/ui';
import { PERMISSIONS } from '@helios/authz';
import { sessionScopeKey, useSessionScopeFence } from '@/hooks/useSessionScope';
import {
  useAiConfiguration,
  useAiConnectionTest,
  useSaveAiConfiguration,
} from './aiSettings.hooks';
import type {
  AiAnthropicConfiguration,
  AiOpenAiConfiguration,
  AiProviderMode,
} from './aiSettings.types';
import { useSettingsDraft } from './useSettingsDraft';
import { SettingsDraftActions } from './SettingsDraftActions';
import { AiSettingsApiError } from './aiSettings.api';
import {
  SettingsField,
  SettingsLoadingState,
  SettingsMutationBanner,
  SettingsSection,
  SettingsToggle,
  settingsControlClass,
} from './settingsChrome';

type ProviderForm = { anthropic: AiAnthropicConfiguration; openai: AiOpenAiConfiguration };
const modes: { value: AiProviderMode; label: string }[] = [
  { value: 'inherit', label: 'Use platform configuration' },
  { value: 'direct', label: 'Use workspace API key' },
  { value: 'disabled', label: 'Disabled for this workspace' },
];
function saveDiagnostic(error: unknown): unknown {
  return error instanceof AiSettingsApiError
    ? `${error.message} (${error.code ?? 'AI_SETTINGS_REQUEST_FAILED'}; HTTP ${error.status})`
    : error;
}
export function AiProviderConfiguration() {
  const query = useAiConfiguration();
  const save = useSaveAiConfiguration();
  const test = useAiConnectionTest();
  const fence = useSessionScopeFence(PERMISSIONS.SETTINGS_MANAGE);
  const keyScope = JSON.stringify(sessionScopeKey(fence.identity));
  const [secrets, setSecrets] = useState<{
    scope: string;
    anthropic: string;
    openai: string;
    removeAnthropic: boolean;
    removeOpenai: boolean;
  } | null>(null);
  const keys =
    secrets?.scope === keyScope
      ? secrets
      : { scope: keyScope, anthropic: '', openai: '', removeAnthropic: false, removeOpenai: false };
  const keyDirty = Boolean(
    keys.anthropic || keys.openai || keys.removeAnthropic || keys.removeOpenai,
  );
  useEffect(() => {
    setSecrets((previous) => (previous?.scope === keyScope && fence.enabled ? previous : null));
  }, [keyScope, fence.enabled]);
  const latest = query.data
    ? {
        form: {
          anthropic: {
            mode: query.data.providers.anthropic.mode,
            model: query.data.providers.anthropic.model,
          },
          openai: {
            mode: query.data.providers.openai.mode,
            model: query.data.providers.openai.model,
            imageModel: query.data.providers.openai.imageModel,
            transcriptionModel: query.data.providers.openai.transcriptionModel,
            speechModel: query.data.providers.openai.speechModel,
          },
        },
        version: query.data.version,
      }
    : null;
  const draft = useSettingsDraft<ProviderForm>(latest, keyDirty);
  const [notice, setNotice] = useState<{
    scope: string;
    message: string;
    error?: boolean;
  } | null>(null);
  const setKey = (patch: Partial<typeof keys>) => setSecrets({ ...keys, ...patch });
  const canManage = Boolean(fence.enabled && query.data?.canManage);
  const busy = save.isPending || test.isPending;
  const inactiveNewKey = Boolean(
    (keys.anthropic.trim() && draft.form?.anthropic.mode !== 'direct') ||
      (keys.openai.trim() && draft.form?.openai.mode !== 'direct'),
  );
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (
      !canManage ||
      busy ||
      inactiveNewKey ||
      !draft.form ||
      draft.version === null ||
      (!draft.dirty && !keyDirty)
    )
      return;
    const origin = fence.identity;
    try {
      const saved = await save.mutateAsync({
        expectedVersion: draft.version,
        providers: {
          anthropic: {
            ...draft.form.anthropic,
            ...(keys.anthropic.trim() ? { apiKey: keys.anthropic.trim() } : {}),
            ...(keys.removeAnthropic ? { removeKey: true } : {}),
          },
          openai: {
            ...draft.form.openai,
            ...(keys.openai.trim() ? { apiKey: keys.openai.trim() } : {}),
            ...(keys.removeOpenai ? { removeKey: true } : {}),
          },
        },
      });
      if (!fence.isCurrent(origin)) return;
      draft.accept({
        form: {
          anthropic: {
            mode: saved.providers.anthropic.mode,
            model: saved.providers.anthropic.model,
          },
          openai: {
            mode: saved.providers.openai.mode,
            model: saved.providers.openai.model,
            imageModel: saved.providers.openai.imageModel,
            transcriptionModel: saved.providers.openai.transcriptionModel,
            speechModel: saved.providers.openai.speechModel,
          },
        },
        version: saved.version,
      });
      setSecrets(null);
      setNotice({
        scope: draft.scope,
        message: `${saved.saveResult?.code === 'AI_CONFIGURATION_UNCHANGED' ? 'Configuration unchanged' : 'Workspace AI configuration saved'} (version ${saved.version}). Active sources: Claude ${saved.providers.anthropic.source}, OpenAI ${saved.providers.openai.source}. Saved keys are hidden. Saving does not test authentication or generation.`,
      });
    } catch {
      /* Keep the draft available for correction; the scoped banner reports the error. */
    }
  };
  const runTest = async (provider: 'anthropic' | 'openai') => {
    if (!canManage || busy || draft.dirty || keyDirty || !query.data) return;
    const origin = fence.identity;
    try {
      const result = await test.mutateAsync({ provider, expectedVersion: query.data.version });
      if (fence.isCurrent(origin))
        setNotice({
          scope: draft.scope,
          message: `${provider === 'anthropic' ? 'Claude' : 'OpenAI'}: ${result.detail}`,
          error: !result.verified,
        });
    } catch {
      /* The scoped mutation banner reports the error. */
    }
  };
  if (query.isLoading) return <SettingsLoadingState label="Loading AI configuration…" />;
  if (!query.data || !draft.form)
    return (
      <ErrorState
        title="AI configuration unavailable"
        description={
          query.error instanceof Error
            ? query.error.message
            : 'Workspace configuration could not be loaded.'
        }
        action={
          <Button size="xs" variant="outline" onClick={() => void query.refetch()}>
            Retry configuration
          </Button>
        }
      />
    );
  const form = draft.form;
  const workspaceAnthropicKey =
    query.data.providers.anthropic.workspaceKeyPresent ??
    (query.data.providers.anthropic.source !== 'platform'
      ? query.data.providers.anthropic.keyPresent
      : undefined);
  const workspaceOpenAiKey =
    query.data.providers.openai.workspaceKeyPresent ??
    (query.data.providers.openai.source !== 'platform'
      ? query.data.providers.openai.keyPresent
      : undefined);
  return (
    <SettingsSection
      title="Provider keys & models"
      description="Claude powers business responses and workspace analysis. OpenAI supplies images, transcription, and generated speech."
      actions={
        <Button
          size="xs"
          variant="outline"
          disabled={query.isFetching || busy}
          onClick={() => void query.refetch()}
        >
          Refresh configuration
        </Button>
      }
    >
      <form onSubmit={(event) => void submit(event)} className="grid gap-3">
        <SettingsMutationBanner
          message={notice?.scope === draft.scope ? notice.message : null}
          error={
            saveDiagnostic(save.error) ||
            saveDiagnostic(test.error) ||
            (notice?.scope === draft.scope && notice.error ? notice.message : null)
          }
        />
        {inactiveNewKey ? (
          <p role="alert" className="text-critical text-xs">
            A new key is waiting in a provider that is not using workspace credentials. Select Use
            workspace API key, or revert the draft, before saving.
          </p>
        ) : null}
        <SettingsDraftActions
          dirty={draft.dirty || keyDirty}
          changedOnServer={draft.changedOnServer}
          disabled={busy}
          onRevert={() => {
            draft.accept();
            setSecrets(null);
            setNotice(null);
            save.reset();
            test.reset();
          }}
        />
        <p className="text-muted-foreground text-xs">
          {query.data.credentialStorage.detail} API usage is billed by the provider. A Claude or
          ChatGPT app subscription does not supply an API key.
        </p>
        <p className="text-muted-foreground text-xs">
          Saved version {query.data.version}
          {query.data.updatedAt ? ` · ${query.data.updatedAt}` : ''}. Platform mode uses the API
          server credential and model. Client Launch document analysis has separate server
          credentials and requires Claude platform mode. Claude workspace-key or disabled mode
          pauses that service; these keys do not configure it.
        </p>
        <div className="grid gap-3 lg:grid-cols-2">
          <div
            data-integration-card="ai-provider-anthropic"
            className="border-border grid content-start gap-2 rounded-lg border p-2.5"
          >
            <div className="flex flex-wrap justify-between gap-2">
              <h3 className="text-foreground text-sm font-semibold">Anthropic Claude</h3>
              <span className="text-muted-foreground text-2xs">
                {query.data.providers.anthropic.configured
                  ? 'Credentials configured'
                  : 'Not configured'}{' '}
                · Active source: {query.data.providers.anthropic.source}
              </span>
            </div>
            <p className="text-muted-foreground text-xs">
              Workspace key:{' '}
              {workspaceAnthropicKey === undefined
                ? 'status not reported'
                : workspaceAnthropicKey
                  ? 'saved'
                  : 'not saved'}
              {workspaceAnthropicKey && query.data.providers.anthropic.source !== 'workspace'
                ? ' (inactive)'
                : ''}
              .
              {query.data.providers.anthropic.workspaceCredentialReadable === false
                ? ' Saved credentials cannot currently be read; restore encrypted storage or recover both provider keys.'
                : ''}
            </p>
            <SettingsField label="Claude connection">
              <select
                data-integration-select="ai-provider-anthropic"
                className={settingsControlClass}
                disabled={!canManage || busy}
                value={form.anthropic.mode}
                onChange={(event) =>
                  draft.change({
                    ...form,
                    anthropic: { ...form.anthropic, mode: event.target.value as AiProviderMode },
                  })
                }
              >
                {modes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </SettingsField>
            <SettingsField label="Claude model">
              <input
                className={settingsControlClass}
                required
                maxLength={120}
                disabled={!canManage || busy || form.anthropic.mode !== 'direct'}
                value={form.anthropic.model}
                onChange={(event) =>
                  draft.change({
                    ...form,
                    anthropic: { ...form.anthropic, model: event.target.value },
                  })
                }
              />
            </SettingsField>
            <SettingsField label="Anthropic API key">
              <input
                className={settingsControlClass}
                type="password"
                autoComplete="off"
                spellCheck={false}
                minLength={8}
                maxLength={512}
                disabled={
                  !canManage ||
                  busy ||
                  !query.data.credentialStorage.configured ||
                  form.anthropic.mode !== 'direct' ||
                  keys.removeAnthropic
                }
                value={keys.anthropic}
                placeholder={
                  workspaceAnthropicKey
                    ? 'Workspace key saved · enter a replacement'
                    : 'Enter Anthropic API key'
                }
                onChange={(event) => setKey({ anthropic: event.target.value })}
              />
            </SettingsField>
            {workspaceAnthropicKey && canManage ? (
              <SettingsToggle
                label="Remove workspace Anthropic key"
                disabled={busy || Boolean(keys.anthropic)}
                checked={keys.removeAnthropic}
                onChange={(removeAnthropic) => setKey({ removeAnthropic })}
              />
            ) : null}
            <div>
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={
                  !canManage ||
                  busy ||
                  draft.dirty ||
                  keyDirty ||
                  !query.data.providers.anthropic.configured
                }
                onClick={() => void runTest('anthropic')}
              >
                Check Claude authentication
              </Button>
            </div>
          </div>
          <div
            data-integration-card="ai-provider-openai"
            className="border-border grid content-start gap-2 rounded-lg border p-2.5"
          >
            <div className="flex flex-wrap justify-between gap-2">
              <h3 className="text-foreground text-sm font-semibold">OpenAI</h3>
              <span className="text-muted-foreground text-2xs">
                {query.data.providers.openai.configured
                  ? 'Credentials configured'
                  : 'Not configured'}{' '}
                · Active source: {query.data.providers.openai.source}
              </span>
            </div>
            <p className="text-muted-foreground text-xs">
              Workspace key:{' '}
              {workspaceOpenAiKey === undefined
                ? 'status not reported'
                : workspaceOpenAiKey
                  ? 'saved'
                  : 'not saved'}
              {workspaceOpenAiKey && query.data.providers.openai.source !== 'workspace'
                ? ' (inactive)'
                : ''}
              .
              {query.data.providers.openai.workspaceCredentialReadable === false
                ? ' Saved credentials cannot currently be read; restore encrypted storage or recover both provider keys.'
                : ''}
            </p>
            <SettingsField label="OpenAI connection">
              <select
                data-integration-select="ai-provider-openai"
                className={settingsControlClass}
                disabled={!canManage || busy}
                value={form.openai.mode}
                onChange={(event) =>
                  draft.change({
                    ...form,
                    openai: { ...form.openai, mode: event.target.value as AiProviderMode },
                  })
                }
              >
                {modes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </SettingsField>
            <div className="grid gap-2 sm:grid-cols-2">
              {(['imageModel', 'transcriptionModel', 'speechModel'] as const).map((field) => (
                <SettingsField
                  key={field}
                  label={
                    {
                      imageModel: 'Image model',
                      transcriptionModel: 'Transcription model',
                      speechModel: 'Speech model',
                    }[field]
                  }
                >
                  <input
                    className={settingsControlClass}
                    required
                    maxLength={120}
                    disabled={!canManage || busy || form.openai.mode !== 'direct'}
                    value={form.openai[field]}
                    onChange={(event) =>
                      draft.change({
                        ...form,
                        openai: { ...form.openai, [field]: event.target.value },
                      })
                    }
                  />
                </SettingsField>
              ))}
            </div>
            <SettingsField label="OpenAI API key">
              <input
                className={settingsControlClass}
                type="password"
                autoComplete="off"
                spellCheck={false}
                minLength={8}
                maxLength={512}
                disabled={
                  !canManage ||
                  busy ||
                  !query.data.credentialStorage.configured ||
                  form.openai.mode !== 'direct' ||
                  keys.removeOpenai
                }
                value={keys.openai}
                placeholder={
                  workspaceOpenAiKey
                    ? 'Workspace key saved · enter a replacement'
                    : 'Enter OpenAI API key'
                }
                onChange={(event) => setKey({ openai: event.target.value })}
              />
            </SettingsField>
            {workspaceOpenAiKey && canManage ? (
              <SettingsToggle
                label="Remove workspace OpenAI key"
                disabled={busy || Boolean(keys.openai)}
                checked={keys.removeOpenai}
                onChange={(removeOpenai) => setKey({ removeOpenai })}
              />
            ) : null}
            <div>
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={
                  !canManage ||
                  busy ||
                  draft.dirty ||
                  keyDirty ||
                  !query.data.providers.openai.configured
                }
                onClick={() => void runTest('openai')}
              >
                Check OpenAI authentication
              </Button>
            </div>
          </div>
        </div>
        <p className="text-muted-foreground text-2xs">
          Workspace keys stay encrypted and are never shown again. Save changes before checking
          authentication. The model-list check sends no workspace content and does not test
          generation, selected-model access, credits or feature readiness. An unchecked provider can
          still be used by enabled features.
        </p>
        {canManage ? (
          <div>
            <Button
              type="submit"
              size="xs"
              disabled={busy || inactiveNewKey || (!draft.dirty && !keyDirty)}
            >
              {save.isPending ? 'Saving…' : 'Save provider configuration'}
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">
            Workspace settings permission is required to change models or keys.
          </p>
        )}
      </form>
    </SettingsSection>
  );
}
